<template>
  <Dialog
    :model-value="visible"
    title="Edit Unassigned Program"
    dialog-type="primary"
    dialog-size="medium"
    :close-on-overlay="false"
    @update:model-value="(v) => { if (!v) closeDialog() }"
    @close="closeDialog"
  >
    <!-- Dialog Content -->
    <div class="dialog-content">
      <div class="content-wrapper">
        <!-- White Card with Shadow -->
        <div class="program-card">
          <!-- Program Details Header -->
          <div class="card-header">
            <img src="@/assets/MyWorkBench/Workflow/program_detail.svg" alt="Program Details" class="section-icon" />
            <span class="card-title">Program Details</span>
          </div>

          <!-- Form Fields -->
          <div class="card-body">
            <!-- Program Name -->
            <Input
              v-model="formData.name"
              InputTitle="Program Name"
              :is-required-text="'(Required)'"
              placeholder="Weight Management Program"
            />

            <!-- Program Description -->
            <TextArea
              v-model="formData.description"
              InputTitle="Program Description"
              :maxlength="1000"
              :rows="4"
              :clearable="true"
              placeholder="No description provided"
            />

            <!-- Duration Type (Full Row) -->
            <div class="duration-type-section">
              <SingleSelector
                v-model="formData.durationType"
                :has-label="true"
                size="large"
                InputTitle="Duration Type"
                :isRequiredText="'(Required)'"
                placeholder="Please Select"
                :options="durationTypeOptions"
                @change="handleDurationTypeChange"
              />

              <!-- Fixed Duration: Start and End Date -->
              <div v-if="formData.durationType === 'fixed'" class="fixed-duration-fields">
                <div class="duration-label">
                  Duration <span class="required">(Required)</span>
                </div>
                <div class="date-fields-container">
                  <div class="date-field">
                    <el-date-picker
                      v-model="formData.startDate"
                      type="date"
                      placeholder="Start Date"
                      class="duration-date-picker"
                    />
                  </div>
                  <div class="date-field">
                    <el-date-picker
                      v-model="formData.endDate"
                      type="date"
                      placeholder="End Date"
                      class="duration-date-picker"
                    />
                  </div>
                </div>
              </div>

              <!-- Rolling Duration: Duration Value and Unit -->
              <div v-if="formData.durationType === 'rolling'" class="rolling-duration-fields">
                <div class="duration-label">
                  Duration <span class="required">(Required)</span>
                </div>
                <div class="rolling-container">
                  <div class="rolling-value-field">
                    <el-input
                      v-model.number="formData.durationValue"
                      type="number"
                      placeholder="0"
                      min="0"
                      class="duration-value-input"
                    />
                  </div>
                  <div class="rolling-unit-field">
                    <el-select
                      v-model="formData.durationUnit"
                      placeholder="Unit"
                      class="duration-unit-select"
                      size="large"
                    >
                      <el-option label="Day" value="days" />
                      <el-option label="Week" value="weeks" />
                      <el-option label="Month" value="months" />
                    </el-select>
                  </div>
                </div>
              </div>
            </div>

            <!-- Program Price (Full Row) -->
            <Input
              v-model="formData.price"
              InputTitle="Program Price"
              placeholder="$ 1,299.99"
            />

            <!-- Enrollment Control (Full Row) -->
            <div class="enrollment-control-section">
              <div class="enrollment-control-header">
                <span class="control-label">Enrollment Control</span>
                <div class="toggle-group">
                  <span class="toggle-text">OFF</span>
                  <el-switch
                    v-model="formData.enrollmentControl"
                    class="enrollment-switch"
                  />
                  <span class="toggle-text">ON</span>
                </div>
              </div>

              <!-- Maximum Enrollment (shown when enrollment control is ON) -->
              <div v-if="formData.enrollmentControl" class="max-enrollment-field">
                <div class="enrollment-field-row">
                  <div class="enrollment-field-label">
                    <span>Limit Patient Number to</span>
                    <span class="required">(Required)</span>
                  </div>
                  <input
                    v-model.number="formData.maxEnrollment"
                    type="number"
                    class="enrollment-input"
                    placeholder="0"
                    min="1"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <ResponsiveButton button_type="outlined" built_in_theme="error" size="medium" @click="closeDialog">Cancel</ResponsiveButton>
      <ResponsiveButton button_type="filled" built_in_theme="dark" size="medium" @click="handleSave">Save and Exit</ResponsiveButton>
    </template>
  </Dialog>
</template>

<script>
import { notify } from '@/utils/notify';
import { updateWorkflowApi } from '@/api/workflowManage';

export default {
  name: 'EditProgramDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    program: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      durationTypeOptions: [
        { label: 'Fixed', value: 'fixed' },
        { label: 'Rolling', value: 'rolling' }
      ],
      formData: {
        name: '',
        description: '',
        durationType: '',
        startDate: '',
        endDate: '',
        durationValue: '',
        durationUnit: '',
        price: '',
        enrollmentControl: false,
        maxEnrollment: null
      }
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.program) {
        // Store a local copy of the program to prevent issues if parent clears it
        this.localProgram = JSON.parse(JSON.stringify(this.program));
        this.loadProgramData();
      }
    }
  },
  methods: {
    loadProgramData() {
      if (!this.localProgram) return;
      
      this.formData.name = this.localProgram.name || '';
      this.formData.description = this.localProgram.description || '';
      
      // Handle duration data from backend structure
      if (this.localProgram.duration) {
        this.formData.durationType = this.localProgram.duration.type || '';
        if (this.localProgram.duration.type === 'fixed') {
          this.formData.startDate = this.localProgram.duration.start_date || '';
          this.formData.endDate = this.localProgram.duration.end_date || '';
        } else if (this.localProgram.duration.type === 'rolling') {
          this.formData.durationValue = this.localProgram.duration.value || '';
          this.formData.durationUnit = this.localProgram.duration.unit || '';
        }
      } else {
        // Fallback to old structure if duration object doesn't exist
        this.formData.durationType = this.localProgram.duration_type || '';
        this.formData.startDate = this.localProgram.start_date || '';
        this.formData.endDate = this.localProgram.end_date || '';
        this.formData.durationValue = this.localProgram.durationValue || '';
        this.formData.durationUnit = this.localProgram.durationUnit || '';
      }
      
      this.formData.price = this.localProgram.price ? `$ ${this.localProgram.price}` : '';
      this.formData.enrollmentControl = this.localProgram.maximum_enrollment !== null && this.localProgram.maximum_enrollment !== undefined;
      this.formData.maxEnrollment = this.localProgram.maximum_enrollment || null;
    },

    handleDurationTypeChange() {
      // Clear duration-specific fields when type changes
      this.formData.startDate = '';
      this.formData.endDate = '';
      this.formData.durationValue = '';
      this.formData.durationUnit = '';
    },

    closeDialog() {
      this.$emit('close');
    },

    async handleSave() {
      // Validate required fields
      if (!this.formData.name.trim()) {
        notify({
          title: 'Error',
          message: 'Program name is required',
          type: 'error',
          showClose: true
        });
        return;
      }

      if (!this.formData.durationType) {
        notify({
          title: 'Error',
          message: 'Duration type is required',
          type: 'error',
          showClose: true
        });
        return;
      }

      try {
        // Validate program ID exists
        if (!this.localProgram || !this.localProgram.id) {
          console.error('Program ID is missing:', this.localProgram);
          notify({
            title: 'Error',
            message: 'Program ID is missing. Cannot update program.',
            type: 'error',
            showClose: true
          });
          return;
        }

        // Parse price
        const priceValue = this.formData.price.replace(/[$,\s]/g, '');
        const price = priceValue ? parseFloat(priceValue) : 0;

        // Build duration object based on type
        let duration = {};
        if (this.formData.durationType === 'fixed') {
          duration = {
            type: 'fixed',
            start_date: this.formData.startDate || null,
            end_date: this.formData.endDate || null
          };
        } else if (this.formData.durationType === 'rolling') {
          duration = {
            type: 'rolling',
            value: this.formData.durationValue || 0,
            unit: this.formData.durationUnit || 'days'
          };
        }

        const updateData = {
          name: this.formData.name.trim(),
          description: this.formData.description.trim(),
          duration: duration,
          price: price,
          maximum_enrollment: this.formData.enrollmentControl ? this.formData.maxEnrollment : null
        };

        await updateWorkflowApi(this.localProgram.id, updateData);

        notify({
          title: 'Success',
          message: 'Program updated successfully',
          type: 'success',
          showClose: true
        });

        // Emit success event first, then close
        this.$emit('success', {
          programId: this.localProgram.id,
          updatedData: updateData
        });
        
        // Close dialog - this will trigger parent to reopen ProgramAssigneeDialog
        this.$emit('close');
      } catch (error) {
        console.error('Error updating program:', error);
        notify({
          title: 'Error',
          message: `Failed to update program: ${error.response?.data?.message || error.message}`,
          type: 'error',
          showClose: true
        });
      }
    }
  }
};
</script>

<style scoped>
/* Dialog Header */
.dialog-header {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 16px 24px;
  background: #004879;
  border-radius: 10px 10px 0 0;
}

.header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.header-title {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: white;
  margin: 0;
}

.close-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  opacity: 0.8;
}

/* Dialog Content */
.dialog-content {
  padding: 24px 32px;
  background: #F9FBFD;
  max-height: calc(80vh - 200px);
  overflow-y: auto;
}

.content-wrapper {
  padding: 8px 16px;
}

/* Program Card */
.program-card {
  background: white;
  border-radius: 10px;
  padding: 12px 16px;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.25);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
}

.card-title {
  font-family: 'Avenir', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 24px;
  letter-spacing: 0.15px;
  color: #333;
}

.card-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 12px;
}

/* Duration Type Section */
.duration-type-section {
  display: flex;
  flex-direction: column;
}

/* SingleSelector height override */
.duration-type-section :deep(.new-libary-single-selector .el-input__wrapper) {
  height: 40px;
}

.duration-type-section :deep(.new-libary-single-selector .el-input__inner) {
  height: 40px;
  line-height: 40px;
}

.field-label {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4a4a4a;
  margin-bottom: 4px;
}

.field-label .required {
  color: #d10000;
}

.duration-type-select {
  width: 100%;
}

.duration-type-select :deep(.el-input__wrapper) {
  height: 40px;
}

/* Fixed Duration Fields */
.fixed-duration-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}

.duration-label {
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4a4a4a;
}

.duration-label .required {
  color: #d10000;
}

.date-fields-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.date-field {
  flex: 1;
  min-width: 0;
}

:deep(.duration-date-picker) {
  width: 100%;
}

:deep(.duration-date-picker .el-input__wrapper) {
  height: 40px;
  padding: 8px 12px;
  background: #FFFFFF;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
  box-shadow: none;
}

:deep(.duration-date-picker .el-input__inner) {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
}

:deep(.duration-date-picker .el-input__inner::placeholder) {
  color: #999;
}

/* Rolling Duration Fields */
.rolling-duration-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}

.rolling-container {
  display: flex;
  gap: 8px;
  align-items: center;
}

.rolling-value-field {
  flex: 1;
  min-width: 0;
}

.rolling-unit-field {
  flex: 1;
  min-width: 0;
}

:deep(.duration-value-input .el-input__wrapper) {
  height: 40px;
  padding: 8px 12px;
  background: #FFFFFF;
  border: 1px solid #C9C9C9;
  border-radius: 4px;
  box-shadow: none;
}

:deep(.duration-value-input .el-input__inner) {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
}

:deep(.duration-value-input .el-input__inner::placeholder) {
  color: #999;
}

:deep(.duration-unit-select .el-input__wrapper) {
  height: 40px;
  padding: 8px;
  background: #FFFFFF;
  border: 1px solid #C4C4C4;
  border-radius: 4px;
  box-shadow: none;
}

:deep(.duration-unit-select .el-input__inner) {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
}

:deep(.duration-unit-select .el-select__placeholder) {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #999;
}

/* Enrollment Control Section */
.enrollment-control-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.enrollment-control-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 22px;
}

.control-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #4a4a4a;
}

.toggle-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.toggle-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
}

.enrollment-switch {
  --el-switch-on-color: #374957;
  --el-switch-off-color: #f9fbfd;
  --el-switch-border-color: #374957;
}

/* Maximum Enrollment Field */
.max-enrollment-field {
  margin-top: 12px;
}

.enrollment-field-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.enrollment-field-label {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #4a4a4a;
  width: 300px;
}

.enrollment-field-label .required {
  color: #d10000;
}

.enrollment-input {
  flex: 1;
  height: 40px;
  padding: 8px 12px;
  background: white;
  border: 1px solid #c9c9c9;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
}

.enrollment-input:focus {
  outline: none;
  border-color: #004879;
}

.enrollment-input::placeholder {
  color: #999;
}

.section-icon {
  width: 20px;
  height: 20px;
}

/* Element Plus Select Height Override */
:deep(.el-select .el-input__wrapper) {
  min-height: 40px;
  height: 40px;
}

:deep(.el-input--large .el-input__wrapper) {
  min-height: 40px;
  height: 40px;
}

/* Dialog Footer */
.dialog-footer {
  border-top: 1px dashed #c4c4c4;
  padding: 24px;
  background: #F9FBFD;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
